import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Search, FileText, RefreshCw, ChevronDown, ChevronUp, X, Activity, AlertCircle, Loader2,
  Clock, Smartphone, Zap, Users, Calendar, TrendingUp, PlayCircle, Settings, AlertTriangle,
  Download, BarChart3, ArrowRight
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ultronTuringApi } from '../services/ultronTuringApi';
import { getImportedDevices } from '../services/userPreferencesApi';
import { useTranslation } from '../contexts/LanguageContext';
import { logger } from '../utils/logger';

// ==================== Interfaces ====================

interface DeviceInfo {
  sn: string;
  deviceId?: string;
  name?: string;
  displayName?: string;
  nickname?: string;
  type?: string;
  groupId?: string;
  groupName?: string;
}

interface DeviceLogsPageProps {
  devices?: DeviceInfo[];
}

interface AnalysisStats {
  total: number;
  dateRange: {
    start: string;
    end: string;
  };
  averagePerDay: number;
}

interface SourceAnalysis {
  app: number;
  api: number;
  device: number;
  manual: number;
  scene: number;
  automation: number;
  sceneDistribution?: Record<string, number>;
  total: number;
}

interface TimePatterns {
  hourlyDistribution: Record<string, number>;
  weeklyDistribution: Record<string, number>;
  mostActiveHour: number;
  mostActiveDay: {
    day: number;
    name: string;
    count: number;
  };
}

interface RecentEvent {
  timestamp: number;
  time: string;
  type: string;
  event: string;
  source: string;
  details: Record<string, unknown>;
}

interface AnomalyEvent {
  time: string;
  action: {
    type: string;
    value: unknown;
    displayValue: string;
  };
}

interface Anomaly {
  type: string;
  description: string;
  timestamp: number;
  count: number;
  timeSpan: number;
  events: AnomalyEvent[];
}

interface CommonValue {
  value: string;
  count: number;
}

interface FollowUpQuestion {
  type: string;
  question: string;
  description: string;
}

interface AnalysisResult {
  deviceSn: string;
  period: string;
  summary: string;
  analysis?: {
    stats: AnalysisStats;
    sourceAnalysis: SourceAnalysis;
    timePatterns: TimePatterns;
    behaviorPatterns?: {
      commonValues: Record<string, CommonValue[]>;
      anomalies: Anomaly[];
    };
  };
  recentEvents?: RecentEvent[];
  report: string | {
    summary?: string;
    detailed?: string;
  };
  followUpQuestions?: FollowUpQuestion[];
}

// ==================== Chart Colors ====================

const SOURCE_COLORS: Record<string, string> = {
  app: '#6366f1',      // indigo
  scene: '#a855f7',    // purple
  automation: '#22c55e', // green
  manual: '#f59e0b',   // amber
  api: '#3b82f6',      // blue
  device: '#64748b',   // slate
};

const CHART_COLORS = ['#6366f1', '#a855f7', '#22c55e', '#f59e0b', '#3b82f6', '#ec4899', '#14b8a6'];

// ==================== Sub Components ====================

// Stats Card Component
const StatsCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  color?: string;
}> = ({ icon, label, value, subValue, color = 'indigo' }) => {
  const colorClasses: Record<string, string> = {
    indigo: 'from-indigo-500/20 to-indigo-600/10 border-indigo-500/30 text-indigo-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400',
  };
  
  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4 flex items-center gap-4`}>
      <div className={`p-3 rounded-lg bg-${color}-500/20`}>
        {icon}
      </div>
      <div>
        <div className={`text-2xl font-bold ${colorClasses[color].split(' ').pop()}`}>
          {value}
        </div>
        <div className="text-xs text-slate-400">{label}</div>
        {subValue && <div className="text-xs text-slate-500 mt-0.5">{subValue}</div>}
      </div>
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, defaultOpen = true, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-slate-800/30 rounded-xl border border-slate-700/30 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-700/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-indigo-400">{icon}</div>
          <span className="font-medium text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-700/30">
          {children}
        </div>
      )}
    </div>
  );
};

// Source Pie Chart Component
const SourcePieChart: React.FC<{ sourceAnalysis: SourceAnalysis }> = ({ sourceAnalysis }) => {
  const data = [
    { name: 'App 操作', value: sourceAnalysis.app, color: SOURCE_COLORS.app },
    { name: '情境', value: sourceAnalysis.scene, color: SOURCE_COLORS.scene },
    { name: '自動化', value: sourceAnalysis.automation, color: SOURCE_COLORS.automation },
    { name: '本機操作', value: sourceAnalysis.manual, color: SOURCE_COLORS.manual },
    { name: 'API', value: sourceAnalysis.api, color: SOURCE_COLORS.api },
    { name: '設備', value: sourceAnalysis.device, color: SOURCE_COLORS.device },
  ].filter(d => d.value > 0);

  if (data.length === 0) {
    return <div className="text-center text-slate-400 py-8">無操作來源資料</div>;
  }

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="flex flex-col lg:flex-row items-center gap-4">
      <div className="w-full lg:w-1/2 h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1e293b', 
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#ffffff'
              }}
              itemStyle={{
                color: '#ffffff'
              }}
              labelStyle={{
                color: '#ffffff'
              }}
              formatter={(value, name) => {
                if (typeof value !== 'number') return ['--', typeof name === 'string' ? name : ''];
                return [`${value} 次 (${((value / total) * 100).toFixed(1)}%)`, typeof name === 'string' ? name : ''];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="w-full lg:w-1/2 space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: item.color }}
              />
              <span className="text-slate-300">{item.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-white">{item.value} 次</span>
              <span className="text-slate-500">({((item.value / total) * 100).toFixed(0)}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Hourly Distribution Bar Chart Component
const HourlyBarChart: React.FC<{ hourlyDistribution: Record<string, number> }> = ({ hourlyDistribution }) => {
  const data = Object.entries(hourlyDistribution).map(([hour, count]) => ({
    hour: `${hour}時`,
    count,
    hourNum: parseInt(hour),
  })).sort((a, b) => a.hourNum - b.hourNum);

  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <XAxis 
            dataKey="hour" 
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            interval={2}
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
            domain={[0, maxCount]}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1e293b', 
              border: '1px solid #334155',
              borderRadius: '8px',
              color: '#f1f5f9'
            }}
            formatter={(value) => {
              if (typeof value !== 'number') return ['--', ''];
              return [`${value} 次操作`, ''];
            }}
            labelFormatter={(label) => `${label}`}
          />
          <Bar 
            dataKey="count" 
            fill="#6366f1" 
            radius={[4, 4, 0, 0]}
            maxBarSize={20}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Weekly Distribution Bar Chart Component
const WeeklyBarChart: React.FC<{ 
  weeklyDistribution: Record<string, number>;
  mostActiveDay?: { day: number; name: string; count: number };
}> = ({ weeklyDistribution, mostActiveDay }) => {
  const dayNames = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
  
  const data = Object.entries(weeklyDistribution).map(([day, count]) => ({
    day: dayNames[parseInt(day)] || day,
    dayNum: parseInt(day),
    count,
    isActive: mostActiveDay?.day === parseInt(day),
  })).sort((a, b) => {
    // Sort: Mon(1) to Sun(0), put Sun at end
    const orderA = a.dayNum === 0 ? 7 : a.dayNum;
    const orderB = b.dayNum === 0 ? 7 : b.dayNum;
    return orderA - orderB;
  });

  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <XAxis 
            dataKey="day" 
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
            domain={[0, maxCount]}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1e293b', 
              border: '1px solid #334155',
              borderRadius: '8px',
              color: '#f1f5f9'
            }}
            formatter={(value) => {
              if (typeof value !== 'number') return ['--', ''];
              return [`${value} 次操作`, ''];
            }}
          />
          <Bar 
            dataKey="count" 
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.isActive ? '#a855f7' : '#6366f1'} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {mostActiveDay && (
        <div className="text-center mt-2 text-xs text-slate-400">
          最活躍日：<span className="text-purple-400 font-medium">{mostActiveDay.name}</span> 
          <span className="text-slate-500 ml-1">({mostActiveDay.count} 次)</span>
        </div>
      )}
    </div>
  );
};

// Anomaly Section Component
const AnomalySection: React.FC<{ anomalies: Anomaly[] }> = ({ anomalies }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!anomalies || anomalies.length === 0) {
    return (
      <div className="text-center py-6 text-slate-400">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">未檢測到異常操作模式</p>
      </div>
    );
  }

  const formatTimeSpan = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return '< 1 分鐘';
    return `${minutes} 分鐘`;
  };

  const getAnomalyTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      frequent_switching: '頻繁切換',
      unusual_time: '異常時間',
      high_frequency: '高頻操作',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-3">
      {/* Summary Alert */}
      <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-200">
            檢測到 {anomalies.length} 個異常操作模式
          </p>
          <p className="text-xs text-amber-300/70 mt-0.5">
            建議檢查設備狀態或自動化規則設定
          </p>
        </div>
      </div>

      {/* Anomaly List */}
      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
        {anomalies.map((anomaly, index) => (
          <div 
            key={anomaly.timestamp || index}
            className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden"
          >
            {/* Anomaly Header */}
            <button
              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-slate-700/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  {getAnomalyTypeLabel(anomaly.type)}
                </span>
                <span className="text-xs text-slate-400">
                  {anomaly.count} 次操作 · {formatTimeSpan(anomaly.timeSpan)}
                </span>
              </div>
              {expandedIndex === index ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>

            {/* Anomaly Details */}
            {expandedIndex === index && (
              <div className="px-3 pb-3 pt-1 border-t border-slate-700/50">
                <p className="text-xs text-slate-400 mb-2">{anomaly.description}</p>
                <div className="space-y-1.5">
                  {anomaly.events.map((event, eventIndex) => (
                    <div 
                      key={eventIndex}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span className="text-slate-500 w-24 flex-shrink-0">{event.time}</span>
                      <span className="text-slate-400">{event.action.type}:</span>
                      <span className={`font-medium ${
                        event.action.displayValue === 'occupied' || event.action.displayValue === 'opened' || event.action.displayValue === 'true'
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}>
                        {event.action.displayValue}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Behavior Patterns Section Component
const BehaviorPatternsSection: React.FC<{ 
  commonValues: Record<string, CommonValue[]> 
}> = ({ commonValues }) => {
  if (!commonValues || Object.keys(commonValues).length === 0) {
    return (
      <div className="text-center py-6 text-slate-400">
        <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">無行為模式資料</p>
      </div>
    );
  }

  const getValueLabel = (type: string, value: string): string => {
    const labels: Record<string, Record<string, string>> = {
      occupancy: { 'true': '有人', 'false': '無人' },
      on: { 'true': '開啟', 'false': '關閉' },
      detected: { opened: '開啟', closed: '關閉' },
    };
    return labels[type]?.[value] || value;
  };

  const getValueColor = (value: string): string => {
    if (['true', 'occupied', 'opened', 'on'].includes(value.toLowerCase())) {
      return '#22c55e'; // green
    }
    if (['false', 'unoccupied', 'closed', 'off'].includes(value.toLowerCase())) {
      return '#ef4444'; // red
    }
    return '#6366f1'; // indigo (default)
  };

  return (
    <div className="space-y-4">
      {Object.entries(commonValues).map(([type, values]) => {
        const total = values.reduce((sum, v) => sum + v.count, 0);
        
        return (
          <div key={type} className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-200 capitalize">{type}</span>
              <span className="text-xs text-slate-500">共 {total} 次</span>
            </div>
            
            {/* Progress bars */}
            <div className="space-y-1.5">
              {values.map((item, index) => {
                const percentage = total > 0 ? (item.count / total) * 100 : 0;
                const color = getValueColor(item.value);
                
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-16 text-xs text-slate-400">
                      {getValueLabel(type, item.value)}
                    </div>
                    <div className="flex-1 h-5 bg-slate-700/50 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: color,
                        }}
                      />
                    </div>
                    <div className="w-20 text-right text-xs">
                      <span className="font-medium text-white">{item.count}</span>
                      <span className="text-slate-500 ml-1">({percentage.toFixed(0)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Follow-Up Questions Section Component
const FollowUpQuestionsSection: React.FC<{ 
  questions: FollowUpQuestion[];
  onQuestionClick?: (question: FollowUpQuestion) => void;
}> = ({ questions, onQuestionClick }) => {
  if (!questions || questions.length === 0) return null;

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'anomaly_analysis': return <AlertTriangle className="w-4 h-4" />;
      case 'time_analysis': return <Clock className="w-4 h-4" />;
      case 'export': return <Download className="w-4 h-4" />;
      default: return <ArrowRight className="w-4 h-4" />;
    }
  };

  const getQuestionColor = (type: string): string => {
    switch (type) {
      case 'anomaly_analysis': return 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20';
      case 'time_analysis': return 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/20';
      case 'export': return 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20';
      default: return 'bg-slate-500/10 border-slate-500/30 text-slate-400 hover:bg-slate-500/20';
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {questions.map((q, index) => (
        <button
          key={index}
          onClick={() => onQuestionClick?.(q)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${getQuestionColor(q.type)}`}
          title={q.description}
        >
          {getQuestionIcon(q.type)}
          <span>{q.question}</span>
        </button>
      ))}
    </div>
  );
};

// Timeline Event Component
const TimelineEvent: React.FC<{ event: RecentEvent; isLast: boolean }> = ({ event, isLast }) => {
  // Determine actual source (event text may override source field)
  const getActualSource = (): string => {
    // Check if event text indicates automation
    if (event.event && event.event.includes('自動化')) {
      return 'automation';
    }
    // Check if event text indicates scene
    if (event.event && event.event.includes('情境')) {
      return 'scene';
    }
    return event.source;
  };

  const actualSource = getActualSource();

  const getSourceIcon = (source: string) => {
    switch (source.toLowerCase()) {
      case 'app': return <Smartphone className="w-4 h-4" />;
      case 'scene': return <PlayCircle className="w-4 h-4" />;
      case 'automation': return <Zap className="w-4 h-4" />;
      case 'manual': return <Users className="w-4 h-4" />;
      case 'api': return <Settings className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source: string): string => {
    return SOURCE_COLORS[source.toLowerCase()] || '#64748b';
  };

  const getSourceLabel = (source: string): string => {
    const labels: Record<string, string> = {
      app: 'App 操作',
      scene: '情境',
      automation: '自動化',
      manual: '本機操作',
      api: 'API',
      device: '設備',
    };
    return labels[source.toLowerCase()] || source;
  };

  const formatTime = (timeStr: string): string => {
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('zh-TW', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return timeStr;
    }
  };

  // Format action value for display
  const formatActionValue = (actionType: string, actionValue: unknown): { text: string; isPositive: boolean } => {
    const type = String(actionType).toLowerCase();
    const value = actionValue;
    
    // Occupancy sensor
    if (type === 'occupancy') {
      return value === true || value === 'true' 
        ? { text: '有人', isPositive: true }
        : { text: '無人', isPositive: false };
    }
    
    // Door/window sensor
    if (type === 'detected') {
      if (value === 'opened' || value === 'open') return { text: '已開啟', isPositive: true };
      if (value === 'closed' || value === 'close') return { text: '已關閉', isPositive: false };
      return { text: String(value), isPositive: false };
    }
    
    // Power state
    if (type === 'on' || type === 'power') {
      return value === true || value === 'true'
        ? { text: '開啟', isPositive: true }
        : { text: '關閉', isPositive: false };
    }
    
    // Temperature
    if (type === 'thermostattemperaturesetpoint' || type === 'temperature') {
      return { text: `${value}°C`, isPositive: true };
    }
    
    // Mode
    if (type === 'thermostatmode' || type === 'mode') {
      const modeLabels: Record<string, string> = {
        cool: '冷氣',
        heat: '暖氣',
        dry: '除濕',
        'fan-only': '送風',
        auto: '自動',
      };
      return { text: modeLabels[String(value)] || String(value), isPositive: true };
    }
    
    // Brightness
    if (type === 'brightness') {
      return { text: `亮度 ${value}%`, isPositive: true };
    }
    
    // Default
    return { text: String(value), isPositive: true };
  };

  const formatDetails = (details: Record<string, unknown>, source: string, eventText?: string): React.ReactNode => {
    // Format System log messages to be more human-readable
    if (source.toLowerCase() === 'system') {
      // Check event type first for more accurate display
      if (eventText) {
        const eventLower = eventText.toLowerCase();
        
        // DISCONNECT events
        if (eventLower === 'disconnect') {
          const disconnectType = details.disconnectType ? String(details.disconnectType) : '';
          const description = details.description ? String(details.description) : '';
          let reason = '';
          if (disconnectType === 'MQTT_KEEP_ALIVE_TIMEOUT' || description.includes('timeout')) {
            reason = ' (超時)';
          } else if (disconnectType === 'SERVER' || disconnectType === 'CLIENT_INITIATED_DISCONNECT') {
            reason = '';
          }
          return <span className="text-red-400">已離線{reason}</span>;
        }
        
        // REFRESH_CONNECT events (connection keep-alive/refresh)
        if (eventLower === 'refresh_connect') {
          return <span className="text-blue-400">連線保活</span>;
        }
        
        // CONNECT events
        if (eventLower === 'connect') {
          return <span className="text-green-400">已連線</span>;
        }
      }
      
      // Fallback to checking details
      const detailStr = Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(', ');
      if (detailStr.toLowerCase().includes('disconnect') || 
          details.disconnectType) {
        return <span className="text-red-400">已離線</span>;
      }
      if (detailStr.includes('serviceName:') || 
          detailStr.toLowerCase().includes('connect')) {
        return <span className="text-green-400">已連線</span>;
      }
    }

    // Check if eventText contains automation info (takes priority)
    // e.g., "自動化 (結束植物生長！) 關閉植物生長燈"
    if (eventText && eventText.includes('自動化')) {
      // Extract automation name and action from event text
      const match = eventText.match(/自動化\s*\(([^)]+)\)\s*(.+)/);
      if (match) {
        const automationName = match[1];
        const action = match[2];
        const isPositive = action.includes('開啟') || action.includes('開');
        return (
          <span>
            <span className="text-purple-400">自動化 ({automationName})</span>
            <span className="text-slate-400"> · </span>
            <span className={isPositive ? 'text-green-400' : 'text-red-400'}>
              {action.includes('開啟') ? '開啟' : action.includes('關閉') ? '關閉' : action}
            </span>
          </span>
        );
      }
    }
    
    // For device source, show actionType and actionValue with proper formatting
    if (source.toLowerCase() === 'device' && details.actionType) {
      const actionType = String(details.actionType);
      const actionValue = details.actionValue;
      const { text, isPositive } = formatActionValue(actionType, actionValue);
      
      return (
        <span className={isPositive ? 'text-green-400' : 'text-red-400'}>
          {text}
        </span>
      );
    }
    
    // For app/scene/automation, show user and action
    if (details.actionType && details.actionValue !== undefined) {
      const actionType = String(details.actionType);
      const actionValue = details.actionValue;
      const { text, isPositive } = formatActionValue(actionType, actionValue);
      // Filter out meaningless user values
      const user = details.user && 
        String(details.user) !== 'Device Report' && 
        String(details.user) !== 'Manual/Unknown'
        ? String(details.user) 
        : null;
      
      return (
        <span>
          {user && <span className="text-slate-400">{user} · </span>}
          <span className={isPositive ? 'text-green-400' : 'text-red-400'}>
            {text}
          </span>
        </span>
      );
    }
    
    // Use event text if available and meaningful (but not just "state" or "cmd")
    if (eventText && eventText !== 'state' && eventText !== 'cmd' && eventText.length > 5) {
      return <span className="text-slate-200">{eventText}</span>;
    }
    
    // Fallback: show user if not "Device Report" or "Manual/Unknown"
    if (details.user && 
        String(details.user) !== 'Device Report' && 
        String(details.user) !== 'Manual/Unknown') {
      return String(details.user);
    }
    
    // Last resort: show raw details (excluding meaningless fields)
    const meaningfulDetails = Object.entries(details)
      .filter(([k, v]) => {
        if (k === 'user' && (String(v) === 'Device Report' || String(v) === 'Manual/Unknown')) return false;
        return true;
      });
    
    if (meaningfulDetails.length > 0) {
      return meaningfulDetails.map(([k, v]) => `${k}: ${v}`).join(', ');
    }
    
    return '操作記錄';
  };

  const sourceColor = getSourceColor(actualSource);

  return (
    <div className="flex gap-3">
      {/* Timeline line and dot */}
      <div className="flex flex-col items-center">
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center text-white"
          style={{ backgroundColor: sourceColor }}
        >
          {getSourceIcon(actualSource)}
        </div>
        {!isLast && (
          <div className="w-0.5 flex-1 bg-slate-700 mt-2" />
        )}
      </div>
      
      {/* Event content */}
      <div className={`flex-1 pb-4 ${isLast ? '' : 'border-b border-slate-700/30 mb-3'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span 
            className="text-xs px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: sourceColor }}
          >
            {getSourceLabel(actualSource)}
          </span>
          <span className="text-xs text-slate-500">{formatTime(event.time)}</span>
        </div>
        <div className="mt-1.5 text-sm">
          {formatDetails(event.details, actualSource, event.event)}
        </div>
      </div>
    </div>
  );
};

// ==================== Main Component ====================

const DeviceLogsPage: React.FC<DeviceLogsPageProps> = ({ devices: propDevices = [] }) => {
  useTranslation(); // Keep for potential future use
  
  // State for all devices from all locations
  const [allDevices, setAllDevices] = useState<DeviceInfo[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [period, setPeriod] = useState<'week' | 'twoWeeks'>('week');
  const [format] = useState<'summary' | 'detailed'>('detailed');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Track if we've already fetched devices
  const hasFetchedRef = useRef(false);
  const propDevicesRef = useRef(propDevices);
  propDevicesRef.current = propDevices;
  
  // Fetch all devices from all locations
  const fetchAllDevices = useCallback(async (force: boolean = false) => {
    if (hasFetchedRef.current && !force) {
      return;
    }
    
    setIsLoadingDevices(true);
    setDevicesError(null);
    
    try {
      // Step 1: Get imported devices from Firestore (only show imported devices)
      const importedDevices = await getImportedDevices('intelliSpace');
      logger.log(`📋 [DeviceLogs] Loaded ${importedDevices.length} imported devices from Firestore`);
      
      if (importedDevices.length === 0) {
        logger.warn('⚠️ [DeviceLogs] No imported devices found');
        setAllDevices([]);
        hasFetchedRef.current = true;
        setIsLoadingDevices(false);
        return;
      }
      
      // Step 2: Extract device SNs
      const deviceSNs = importedDevices.map((d: any) => 
        d.deviceSN || d.sn || d.deviceId
      ).filter(Boolean);
      
      if (deviceSNs.length === 0) {
        logger.warn('⚠️ [DeviceLogs] No valid device SNs found in imported devices');
        setAllDevices([]);
        hasFetchedRef.current = true;
        setIsLoadingDevices(false);
        return;
      }
      
      // Step 3: Get unique base SNs and fetch full device info
      const baseSNs = Array.from(new Set(deviceSNs.map((sn: string) => sn.split('#')[0])));
      logger.log(`📋 [DeviceLogs] Querying ${baseSNs.length} devices by SN`);
      
      const devicesResult = await ultronTuringApi.queryDevices(undefined, undefined, {
        sns: baseSNs
      });
      
      logger.log(`📋 [DeviceLogs] QueryDevices returned ${devicesResult.length} devices`);
      
      const deviceMap = new Map<string, DeviceInfo>();
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      devicesResult.forEach((d: any) => {
        const deviceSN = String(d.sn || d.deviceId || '');
        const baseSN = deviceSN.split('#')[0];
        if (!baseSN) return;
        
        // Find matching Firestore device for nickname
        const firestoreDevice = importedDevices.find((fd: any) => {
          const fdSN = fd.deviceSN || fd.sn || fd.deviceId;
          const fdBaseSN = fdSN ? fdSN.split('#')[0] : '';
          return fdSN === deviceSN || fdBaseSN === baseSN;
        });
        
        if (!deviceMap.has(baseSN)) {
          deviceMap.set(baseSN, {
            sn: baseSN,
            deviceId: deviceSN,
            name: String(d.name || d.displayName || ''),
            displayName: d.displayName ? String(d.displayName) : undefined,
            nickname: firestoreDevice?.nickname || (d.nickname ? String(d.nickname) : undefined),
            type: d.type ? String(d.type) : undefined,
            groupId: String(d.groupId || d.group || ''),
            groupName: d.groupName ? String(d.groupName) : (d.location ? String(d.location) : undefined),
          });
        }
      });
      
      const uniqueDevices = Array.from(deviceMap.values());
      logger.log(`📋 [DeviceLogs] Unique devices (by base SN): ${uniqueDevices.length}`);
      
      setAllDevices(uniqueDevices);
      hasFetchedRef.current = true;
    } catch (err) {
      logger.error('❌ [DeviceLogs] Failed to fetch devices:', err);
      setDevicesError(err instanceof Error ? err.message : 'Failed to load devices');
      if (propDevicesRef.current.length > 0) {
        setAllDevices(propDevicesRef.current);
        hasFetchedRef.current = true;
      }
    } finally {
      setIsLoadingDevices(false);
    }
  }, []);
  
  useEffect(() => {
    fetchAllDevices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const devices = allDevices.length > 0 ? allDevices : propDevices;

  const getDeviceDisplayName = (device: DeviceInfo): string => {
    return device.nickname || device.displayName || device.name || device.sn || 'Unknown';
  };

  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return devices;
    
    const query = searchQuery.toLowerCase();
    return devices.filter(device => {
      const sn = (device.sn || '').toLowerCase();
      const name = getDeviceDisplayName(device).toLowerCase();
      const zoneName = (device.groupName || '').toLowerCase();
      return sn.includes(query) || name.includes(query) || zoneName.includes(query);
    });
  }, [devices, searchQuery]);

  // Group devices by zone with count
  const groupedDevices = useMemo(() => {
    const groups: Record<string, DeviceInfo[]> = {};
    
    filteredDevices.forEach(device => {
      const zoneName = device.groupName || '未分類';
      if (!groups[zoneName]) {
        groups[zoneName] = [];
      }
      groups[zoneName].push(device);
    });
    
    // Sort zones alphabetically, but put "未分類" at the end
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === '未分類') return 1;
      if (b === '未分類') return -1;
      return a.localeCompare(b, 'zh-TW');
    });
  }, [filteredDevices]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectDevice = (device: DeviceInfo) => {
    setSelectedDevice(device.sn);
    setSelectedDeviceName(getDeviceDisplayName(device));
    setSearchQuery(device.sn);
    setShowDropdown(false);
    setResult(null);
    setError(null);
  };

  const handleClearSelection = () => {
    setSelectedDevice(null);
    setSelectedDeviceName('');
    setSearchQuery('');
    setResult(null);
    setError(null);
    searchInputRef.current?.focus();
  };

  const handleAnalyze = async () => {
    if (!selectedDevice) {
      setError('請先選擇一個設備');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await ultronTuringApi.analyzeDeviceLogs(selectedDevice, {
        period,
        format
      });

      if (response.success && response.data) {
        setResult(response.data as unknown as AnalysisResult);
        logger.log('📊 Device logs analysis result:', response.data);
      } else {
        const errorMsg = response.error || '';
        if (errorMsg.includes('Unknown request type') || errorMsg.includes('mcp.tool')) {
          setError('此功能尚未在後端啟用。請聯繫管理員以啟用 AnalyzeDeviceLogs API。');
        } else {
          setError(errorMsg || '分析失敗，請稍後再試');
        }
      }
    } catch (err) {
      logger.error('Failed to analyze device logs:', err);
      const errorMsg = err instanceof Error ? err.message : '';
      if (errorMsg.includes('Unknown request type') || errorMsg.includes('mcp.tool')) {
        setError('此功能尚未在後端啟用。請聯繫管理員以啟用 AnalyzeDeviceLogs API。');
      } else {
        setError(errorMsg || '分析時發生錯誤');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Export recent events to CSV
  const handleExportCSV = useCallback(() => {
    if (!result?.recentEvents || result.recentEvents.length === 0) {
      logger.warn('No events to export');
      return;
    }

    const formatActionValue = (actionType: string, actionValue: unknown): string => {
      const type = String(actionType).toLowerCase();
      if (type === 'occupancy') {
        return actionValue === true || actionValue === 'true' ? '有人' : '無人';
      }
      if (type === 'detected') {
        if (actionValue === 'opened' || actionValue === 'open') return '已開啟';
        if (actionValue === 'closed' || actionValue === 'close') return '已關閉';
      }
      if (type === 'on' || type === 'power') {
        return actionValue === true || actionValue === 'true' ? '開啟' : '關閉';
      }
      if (type === 'thermostattemperaturesetpoint' || type === 'temperature') {
        return `${actionValue}°C`;
      }
      return String(actionValue);
    };

    const getSourceLabel = (source: string): string => {
      const labels: Record<string, string> = {
        app: 'App操作',
        scene: '情境',
        automation: '自動化',
        manual: '本機操作',
        api: 'API',
        device: '設備',
      };
      return labels[source.toLowerCase()] || source;
    };

    // CSV Header
    const headers = ['時間', '來源', '操作類型', '操作值', '操作者/備註'];
    
    // CSV Rows
    const rows = result.recentEvents.map(event => {
      const time = new Date(event.time).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const source = getSourceLabel(event.source);
      const actionType = event.details?.actionType ? String(event.details.actionType) : event.type || '';
      const actionValue = event.details?.actionValue !== undefined 
        ? formatActionValue(actionType, event.details.actionValue)
        : event.event || '';
      const user = event.details?.user && String(event.details.user) !== 'Device Report'
        ? String(event.details.user)
        : '';
      
      return [time, source, actionType, actionValue, user];
    });

    // Build CSV content with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = `device_logs_${result.deviceSn || selectedDevice}_${new Date().toISOString().split('T')[0]}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    logger.log(`📥 Exported ${result.recentEvents.length} events to ${filename}`);
  }, [result, selectedDevice]);

  // Handle follow-up question click
  const handleFollowUpClick = useCallback((question: FollowUpQuestion) => {
    logger.log('Follow-up question clicked:', question);
    
    if (question.type === 'export') {
      handleExportCSV();
    } else if (question.type === 'anomaly_analysis') {
      // Scroll to anomaly section
      const anomalySection = document.querySelector('[data-section="anomaly"]');
      anomalySection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (question.type === 'time_analysis') {
      // Scroll to hourly distribution section
      const timeSection = document.querySelector('[data-section="hourly"]');
      timeSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [handleExportCSV]);

  // Helper to get the main source label
  const getMainSourceLabel = (sourceAnalysis: SourceAnalysis): string => {
    const sources = [
      { name: 'App 操作', value: sourceAnalysis.app },
      { name: '情境', value: sourceAnalysis.scene },
      { name: '自動化', value: sourceAnalysis.automation },
      { name: '本機操作', value: sourceAnalysis.manual },
      { name: 'API', value: sourceAnalysis.api },
    ];
    const main = sources.reduce((a, b) => a.value > b.value ? a : b);
    if (main.value === 0) return '-';
    const pct = Math.round((main.value / sourceAnalysis.total) * 100);
    return `${main.name} ${pct}%`;
  };

  // Format date range
  const formatDateRange = (start: string, end: string): string => {
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const fmt = (d: Date) => d.toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' });
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Controls Bar */}
      <div className="flex-shrink-0 px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Device SN Search */}
          <div className="relative w-1/2 min-w-[250px] max-w-[500px]" ref={dropdownRef}>
            {isLoadingDevices ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500 animate-spin" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            )}
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              disabled={isLoadingDevices}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
                if (selectedDevice && e.target.value !== selectedDevice) {
                  setSelectedDevice(null);
                  setSelectedDeviceName('');
                }
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={isLoadingDevices ? '載入設備中...' : devices.length === 0 ? '你的系統中沒有可用設備' : `輸入設備 SN 搜尋 (共 ${devices.length} 台設備)...`}
              className={`w-full pl-9 pr-8 py-2 text-sm border border-slate-700/50 rounded-lg bg-slate-800/50 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${isLoadingDevices ? 'opacity-60' : ''}`}
            />
            {searchQuery && (
              <button
                onClick={handleClearSelection}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Dropdown */}
            {showDropdown && filteredDevices.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                {groupedDevices.map(([zoneName, zoneDevices]) => (
                  <div key={zoneName}>
                    {/* Zone Header with Count */}
                    <div className="sticky top-0 px-3 py-2 bg-slate-800/90 border-b border-slate-700/30 backdrop-blur-sm">
                      <span className="text-xs font-semibold text-indigo-400">{zoneName}</span>
                      <span className="ml-2 text-xs text-slate-500">({zoneDevices.length})</span>
                    </div>
                    {/* Devices in Zone */}
                    {zoneDevices.map((device) => (
                      <button
                        key={device.sn}
                        onClick={() => handleSelectDevice(device)}
                        className={`w-full px-3 py-2 text-left hover:bg-slate-700/50 transition-colors text-sm ${
                          selectedDevice === device.sn ? 'bg-indigo-900/30' : ''
                        }`}
                      >
                        <div className="font-medium text-white truncate">
                          {device.sn}
                        </div>
                        <div className="text-xs text-slate-400 truncate">
                          {getDeviceDisplayName(device)}
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {showDropdown && filteredDevices.length === 0 && searchQuery && (
              <div className="absolute z-50 w-full mt-1 bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg p-3 text-center text-sm text-slate-400">
                找不到符合的設備
              </div>
            )}

            {showDropdown && devices.length === 0 && !isLoadingDevices && !searchQuery && (
              <div className="absolute z-50 w-full mt-1 bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg p-4 text-center">
                <AlertCircle className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <div className="text-sm text-slate-400">你的系統中沒有可用設備</div>
                <div className="text-xs text-slate-500 mt-1">請先在設備管理中匯入設備</div>
              </div>
            )}
          </div>

          {/* Period Selector */}
          <div className="relative">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as 'week' | 'twoWeeks')}
              className="w-[120px] pl-3 pr-8 py-2 text-sm border border-slate-700/50 rounded-lg bg-slate-800/50 text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer"
            >
              <option value="week">最近一週</option>
              <option value="twoWeeks">最近兩週</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Analyze Button */}
          <button
            onClick={handleAnalyze}
            disabled={!selectedDevice || isLoading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              !selectedDevice || isLoading
                ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                分析中...
              </>
            ) : (
              <>
                <Activity className="w-4 h-4" />
                分析
              </>
            )}
          </button>
        </div>

        {/* Selected Device Display */}
        {selectedDevice && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <span className="text-slate-400">已選擇:</span>
            <span className="font-medium text-indigo-400">
              {selectedDevice}
            </span>
            {selectedDeviceName && selectedDeviceName !== selectedDevice && (
              <span className="text-slate-400">
                ({selectedDeviceName})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Devices Loading Error */}
        {devicesError && (
          <div className="rounded-lg p-4 flex items-start gap-3 bg-amber-900/20 border border-amber-800">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-500" />
            <div className="flex-1">
              <p className="font-medium text-sm text-amber-200">設備載入警告</p>
              <p className="text-xs mt-1 text-amber-300">{devicesError}</p>
              <button
                onClick={() => fetchAllDevices(true)}
                className="mt-2 text-xs text-amber-400 hover:underline flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                重試載入
              </button>
            </div>
          </div>
        )}
        
        {/* Error Display */}
        {error && (
          <div className={`rounded-lg p-4 flex items-start gap-3 ${
            error.includes('尚未在後端啟用')
              ? 'bg-amber-900/20 border border-amber-800'
              : 'bg-red-900/20 border border-red-800'
          }`}>
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              error.includes('尚未在後端啟用') ? 'text-amber-500' : 'text-red-500'
            }`} />
            <div>
              <p className={`font-medium text-sm ${
                error.includes('尚未在後端啟用') ? 'text-amber-200' : 'text-red-200'
              }`}>
                {error.includes('尚未在後端啟用') ? '功能未啟用' : '分析失敗'}
              </p>
              <p className={`text-xs mt-1 ${
                error.includes('尚未在後端啟用') ? 'text-amber-300' : 'text-red-300'
              }`}>{error}</p>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="space-y-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl px-5 py-4">
              <h2 className="text-lg font-semibold text-white">設備操作分析報告</h2>
              <p className="text-indigo-100 text-sm mt-1">
                {result.deviceSn} · {result.period === 'week' ? '最近一週' : '最近兩週'}
              </p>
              {result.summary && (
                <p className="text-indigo-100/80 text-sm mt-2 leading-relaxed">
                  {result.summary}
                </p>
              )}
            </div>

            {/* Stats Cards */}
            {result.analysis && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatsCard
                  icon={<TrendingUp className="w-5 h-5 text-indigo-400" />}
                  label="總操作次數"
                  value={result.analysis.stats.total}
                  subValue={formatDateRange(result.analysis.stats.dateRange.start, result.analysis.stats.dateRange.end)}
                  color="indigo"
                />
                <StatsCard
                  icon={<Calendar className="w-5 h-5 text-purple-400" />}
                  label="日均操作"
                  value={result.analysis.stats.averagePerDay.toFixed(1)}
                  subValue="次/天"
                  color="purple"
                />
                <StatsCard
                  icon={<Clock className="w-5 h-5 text-green-400" />}
                  label="最活躍時段"
                  value={`${result.analysis.timePatterns.mostActiveHour}:00`}
                  subValue={result.analysis.timePatterns.mostActiveDay?.name || ''}
                  color="green"
                />
                <StatsCard
                  icon={<Smartphone className="w-5 h-5 text-amber-400" />}
                  label="主要來源"
                  value={getMainSourceLabel(result.analysis.sourceAnalysis)}
                  color="amber"
                />
                    </div>
                  )}

            {/* Anomaly Alert Section - Shown at top if anomalies exist */}
            {result.analysis?.behaviorPatterns?.anomalies && 
             result.analysis.behaviorPatterns.anomalies.length > 0 && (
              <div data-section="anomaly">
                <CollapsibleSection
                  title={`異常操作模式 (${result.analysis.behaviorPatterns.anomalies.length})`}
                  icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
                  defaultOpen={true}
                >
                  <AnomalySection anomalies={result.analysis.behaviorPatterns.anomalies} />
                </CollapsibleSection>
              </div>
            )}

            {/* Charts Section */}
            {result.analysis && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Source Analysis Pie Chart */}
                <CollapsibleSection
                  title="操作來源分析"
                  icon={<Smartphone className="w-5 h-5" />}
                  defaultOpen={true}
                >
                  <SourcePieChart sourceAnalysis={result.analysis.sourceAnalysis} />
                </CollapsibleSection>

                {/* Hourly Distribution Bar Chart */}
                <div data-section="hourly">
                  <CollapsibleSection
                    title="小時分佈"
                    icon={<Clock className="w-5 h-5" />}
                    defaultOpen={true}
                  >
                    <HourlyBarChart hourlyDistribution={result.analysis.timePatterns.hourlyDistribution} />
                  </CollapsibleSection>
                </div>

                {/* Weekly Distribution Bar Chart */}
                <CollapsibleSection
                  title="每週分佈"
                  icon={<Calendar className="w-5 h-5" />}
                  defaultOpen={true}
                >
                  <WeeklyBarChart 
                    weeklyDistribution={result.analysis.timePatterns.weeklyDistribution}
                    mostActiveDay={result.analysis.timePatterns.mostActiveDay}
                  />
                </CollapsibleSection>

                {/* Behavior Patterns */}
                {result.analysis.behaviorPatterns?.commonValues && 
                 Object.keys(result.analysis.behaviorPatterns.commonValues).length > 0 && (
                  <CollapsibleSection
                    title="行為模式分析"
                    icon={<BarChart3 className="w-5 h-5" />}
                    defaultOpen={true}
                  >
                    <BehaviorPatternsSection commonValues={result.analysis.behaviorPatterns.commonValues} />
                  </CollapsibleSection>
                )}
              </div>
            )}

            {/* Recent Events Timeline */}
            {result.recentEvents && result.recentEvents.length > 0 && (
              <CollapsibleSection
                title={`最近操作記錄 (${result.recentEvents.length} 筆)`}
                icon={<Activity className="w-5 h-5" />}
                defaultOpen={true}
              >
                <div className="max-h-[400px] overflow-y-auto pr-2">
                  {result.recentEvents.map((event, index) => (
                    <TimelineEvent 
                      key={event.timestamp || index} 
                      event={event} 
                      isLast={index === result.recentEvents!.length - 1}
                    />
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Scene Distribution if exists */}
            {result.analysis?.sourceAnalysis.sceneDistribution && 
             Object.keys(result.analysis.sourceAnalysis.sceneDistribution).length > 0 && (
              <CollapsibleSection
                title="情境使用分佈"
                icon={<PlayCircle className="w-5 h-5" />}
                defaultOpen={false}
              >
                <div className="space-y-2">
                  {Object.entries(result.analysis.sourceAnalysis.sceneDistribution).map(([scene, count], index) => (
                    <div key={scene} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <span className="text-slate-300 truncate max-w-[200px]">{scene}</span>
                      </div>
                      <span className="font-medium text-white">{count} 次</span>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Follow-Up Questions Section */}
            {result.followUpQuestions && result.followUpQuestions.length > 0 && (
              <div className="bg-slate-800/30 rounded-xl border border-slate-700/30 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowRight className="w-5 h-5 text-indigo-400" />
                  <span className="font-medium text-white">建議進一步分析</span>
                </div>
                <FollowUpQuestionsSection 
                  questions={result.followUpQuestions}
                  onQuestionClick={handleFollowUpClick}
                />
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!result && !error && !isLoading && (
          <div className="bg-[#0C2340]/50 rounded-xl border-2 border-dashed border-slate-700/50 p-12 text-center">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              尚無分析結果
            </h3>
            <p className="text-slate-400 text-sm">
              請選擇一個設備並點擊「分析」按鈕開始分析
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceLogsPage;
